// LIVE TAPE: the scrolling strip of prices and just-detected events on the Convergence
// Board, directly below the fear/greed display and above the cards.
//
// ⚠ One GET to /api/tape per minute, paused while the tab is hidden. The server answers
// from one shared payload, so cost does not grow with viewers, and it never reaches a
// provider on a request. A click opens a tab through the existing navigation; nothing
// here starts a scan.
// ⚠ Display rules: no provider names, no direction words, nothing fabricated. A missing
// value is left out, an item without its required values is dropped, and an empty answer
// hides the strip rather than showing a blank bar.
import { useEffect, useMemo, useRef, useState } from "react";
import { C, font, backendFetch } from "../core";

export const TAPE_POLL_MS = 60_000;
// A failed poll keeps the strip through a blip, but never past this: prices would be
// sitting on screen without the server's freshness rule behind them.
export const TAPE_MAX_AGE_MS = 5 * 60_000;
const WATCHLIST_KEY = "fterm_watchlist";
const CHART_SYMBOL_KEY = "fterm_chart_symbol";
const WATCHLIST_MAX = 50;       // MAX_WATCHLIST_PER_REQUEST in app/services/tape.py
const SECONDS_PER_ITEM = 5;
const MIN_LOOP_S = 30;
const MAX_LOOP_S = 240;
const PRICES_PER_EVENT = 3;     // one event after this many prices, while both last
const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
const KINDS = new Set(["dark_pool", "unusual_options", "complex_order"]);
const SESSION_BADGE = { pre: "PRE", post: "POST" };
// The same last check the server makes on every event's text.
const BANNED = /\b(bought|buy|buys|buying|buyer|buyers|sold|sell|sells|selling|seller|sellers|bullish|bearish|credit|debit|long|short|accumulation|distribution|premium|alpaca|finra|opra|sip|polygon|tradier|yahoo|nasdaq|nyse)\b/i;

const fin = (v) => typeof v === "number" && Number.isFinite(v);
const nf2 = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function readWatchlist() {
  try {
    const raw = JSON.parse(localStorage.getItem(WATCHLIST_KEY));
    if (!Array.isArray(raw)) return [];
    const out = raw.map((x) => (typeof x === "string" ? x : x?.symbol)).filter(Boolean)
      .map((sym) => String(sym).trim().toUpperCase()).filter(Boolean);
    return [...new Set(out)].slice(0, WATCHLIST_MAX);
  } catch {
    return [];
  }
}

export function tapeUrl(watchlist) {
  return watchlist && watchlist.length
    ? `/api/tape?watchlist=${encodeURIComponent(watchlist.join(","))}` : "/api/tape";
}

// The server's answer → what can be stated, or null for nothing to show.
export function sanitizeTape(body) {
  if (!body || body.status !== "ok") return null;
  const prices = (Array.isArray(body.prices) ? body.prices : [])
    .filter((p) => p && typeof p.symbol === "string" && p.symbol && fin(p.price) && p.price > 0);
  const events = (Array.isArray(body.events) ? body.events : [])
    .filter((e) => e && KINDS.has(e.kind) && typeof e.ticker === "string" && e.ticker
      && typeof e.text === "string" && e.text && !BANNED.test(e.text));
  return prices.length || events.length ? { prices, events } : null;
}

// Prices and events mixed along the strip, each kind keeping the server's order.
export function mixItems(data) {
  if (!data) return [];
  const out = [];
  let e = 0;
  data.prices.forEach((p, i) => {
    out.push({ ...p, kind: "price" });
    if ((i + 1) % PRICES_PER_EVENT === 0 && e < data.events.length) out.push(data.events[e++]);
  });
  while (e < data.events.length) out.push(data.events[e++]);
  return out;
}

export function fmtAge(seconds) {
  if (!fin(seconds) || seconds < 0) return "";
  if (seconds < 60) return "just now";
  const m = Math.floor(seconds / 60);
  return m < 60 ? `${m}m ago` : `${Math.floor(m / 60)}h ago`;
}

export function fmtChange(pct) {
  if (!fin(pct)) return "";
  return `${pct > 0 ? "+" : ""}${nf2.format(pct)}%`;
}

export function loopSeconds(count) {
  return Math.min(MAX_LOOP_S, Math.max(MIN_LOOP_S, count * SECONDS_PER_ITEM));
}

// A click: a price opens its chart; an event opens the tab that shows it, for that
// ticker. Every destination reads what it already caches, so no scan starts here.
export function openTapeItem(item, nav) {
  if (!item) return;
  if (item.kind === "price") {
    try { localStorage.setItem(CHART_SYMBOL_KEY, item.symbol); } catch { /* private mode */ }
    nav.setTab("CHARTS");
  } else if (item.kind === "dark_pool") {
    nav.setDpFocus({ symbol: item.ticker, nonce: Date.now() });
    nav.setTab("DARK POOL");
  } else if (item.kind === "unusual_options") {
    nav.setUoFocus({ symbol: item.ticker, nonce: Date.now() });
    nav.setTab("UNUSUAL OPTIONS");
  } else if (item.kind === "complex_order") {
    nav.setComplexLink(item.link || null);
    nav.setTab("COMPLEX ORDERS");
  }
}

function describe(item) {
  if (item.kind === "price") {
    const change = fmtChange(item.change_pct);
    const badge = item.label === "close" ? "close" : SESSION_BADGE[item.session]?.toLowerCase();
    return `${item.symbol} ${nf2.format(item.price)}${change ? ` ${change}` : ""}${badge ? `, ${badge}` : ""}. Open chart.`;
  }
  const when = item.prior_session ? item.tag : fmtAge(item.age_s);
  return `${item.ticker}: ${item.text}${when ? `, ${when}` : ""}. Open details.`;
}

const badgeStyle = { fontSize: 8, fontWeight: 700, letterSpacing: "0.08em", color: C.textDim,
  border: `1px solid ${C.border}`, borderRadius: 2, padding: "0 3px" };

function Item({ item, onOpen, clone }) {
  const body = item.kind === "price" ? (
    <>
      <span data-f="symbol" style={{ color: C.text, fontWeight: 700 }}>{item.symbol}</span>
      <span data-f="price" style={{ color: C.text }}>{nf2.format(item.price)}</span>
      {fin(item.change_pct) && (
        <span data-f="change" style={{ color: item.change_pct > 0 ? C.green : item.change_pct < 0 ? C.red : C.textMid }}>
          {fmtChange(item.change_pct)}
        </span>
      )}
      {item.label === "close"
        ? <span data-f="close" style={badgeStyle}>CLOSE</span>
        : SESSION_BADGE[item.session] ? <span data-f="session" style={badgeStyle}>{SESSION_BADGE[item.session]}</span> : null}
    </>
  ) : (
    <>
      <span data-f="ticker" style={{ color: C.amber, fontWeight: 700 }}>{item.ticker}:</span>
      <span data-f="text" style={{ color: C.textMid }}>{item.text}</span>
      {item.prior_session
        ? <span data-f="tag" style={badgeStyle}>{String(item.tag || "").toUpperCase()}</span>
        : fmtAge(item.age_s) ? <span data-f="age" style={{ color: C.textDim }}>{fmtAge(item.age_s)}</span> : null}
    </>
  );
  return (
    <button type="button" data-tape-item={item.kind} aria-label={describe(item)} title={describe(item)}
      tabIndex={clone ? -1 : undefined} onClick={() => onOpen?.(item)}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0, height: 32, padding: "0 12px",
        background: "none", border: "none", borderRight: `1px solid ${C.border}`, cursor: "pointer",
        whiteSpace: "nowrap", font: "inherit", fontSize: 11 }}>
      {body}
    </button>
  );
}

const TAPE_CSS = `
@keyframes qs-tape-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
.qs-tape-view { flex: 1 1 auto; min-width: 0; overflow: hidden; }
.qs-tape-track { display: inline-flex; width: max-content; animation: qs-tape-scroll var(--qs-tape-loop) linear infinite; }
.qs-tape-group { display: inline-flex; min-width: 100vw; }
.qs-tape-view:hover .qs-tape-track, .qs-tape-view:focus-within .qs-tape-track,
.qs-tape-track[data-paused="true"] { animation-play-state: paused; }
.qs-tape-view[data-motion="static"] { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.qs-tape-view[data-motion="static"] .qs-tape-track { animation: none; }
.qs-tape-view[data-motion="static"] .qs-tape-group { min-width: 0; }
@media (prefers-reduced-motion: reduce) {
  .qs-tape-track { animation: none; }
  .qs-tape-view { overflow-x: auto; -webkit-overflow-scrolling: touch; }
}`;

function useReducedMotion() {
  const [reduced, setReduced] = useState(() => {
    try { return !!window.matchMedia?.(REDUCED_MOTION)?.matches; } catch { return false; }
  });
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia?.(REDUCED_MOTION); } catch { return undefined; }
    if (!mq?.addEventListener) return undefined;
    const on = (e) => setReduced(!!e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduced;
}

export function LiveTape({ onOpen }) {
  const [data, setData] = useState(null);
  const [paused, setPaused] = useState(false);
  const reduced = useReducedMotion();
  const lastGood = useRef(0);

  useEffect(() => {
    let alive = true;
    let timer = null;
    const poll = async () => {
      if (document.hidden) return;
      let res = null;
      try { res = await backendFetch(null, tapeUrl(readWatchlist())); } catch { res = null; }
      let body = null;
      if (res && res.ok) {
        try { body = await res.json(); } catch { body = null; }
      }
      if (!alive) return;
      if (body) {
        lastGood.current = Date.now();
        setData(sanitizeTape(body));
      } else if (Date.now() - lastGood.current > TAPE_MAX_AGE_MS) {
        setData(null);
      }
    };
    const start = () => {
      if (timer === null) timer = setInterval(poll, TAPE_POLL_MS);
    };
    const stop = () => {
      if (timer !== null) clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        poll();
        start();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    if (!document.hidden) {
      poll();
      start();
    }
    return () => {
      alive = false;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const items = useMemo(() => mixItems(data), [data]);
  if (!items.length) return null;

  return (
    <section data-tape aria-label="Market tape" style={{ display: "flex", alignItems: "center", background: C.panelAlt,
      border: `1px solid ${C.border}`, borderRadius: 3, marginBottom: 10, fontFamily: font, width: "100%", minWidth: 0 }}>
      <style>{TAPE_CSS}</style>
      <div className="qs-tape-view" data-tape-view data-motion={reduced ? "static" : "scroll"}>
        <div className="qs-tape-track" data-tape-track data-paused={paused ? "true" : "false"}
          style={{ "--qs-tape-loop": `${loopSeconds(items.length)}s` }}>
          <div className="qs-tape-group" data-tape-group>
            {items.map((it, i) => <Item key={`${it.kind}-${it.symbol || it.ticker}-${i}`} item={it} onOpen={onOpen} />)}
          </div>
          {!reduced && (
            <div className="qs-tape-group" data-tape-clone aria-hidden="true">
              {items.map((it, i) => <Item key={`c-${it.kind}-${it.symbol || it.ticker}-${i}`} item={it} onOpen={onOpen} clone />)}
            </div>
          )}
        </div>
      </div>
      {!reduced && (
        <button type="button" data-tape-pause aria-pressed={paused} onClick={() => setPaused((p) => !p)}
          aria-label={paused ? "Resume tape" : "Pause tape"}
          style={{ flexShrink: 0, minWidth: 44, height: 32, background: C.panelAlt, border: "none",
            borderLeft: `1px solid ${C.border}`, color: C.textMid, cursor: "pointer", font: "inherit", fontSize: 10,
            fontWeight: 700, letterSpacing: "0.06em" }}>
          {paused ? "PLAY" : "PAUSE"}
        </button>
      )}
    </section>
  );
}
